import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import logo from '../../assets/DatatwinIcon.png';
import bgImage from '../../assets/login-bg.jpg';
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Fade,
  Divider,
  Paper,
  Avatar,
  CircularProgress
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { login, onRequestPassword, logout } from '../../redux/Login/loginActions';
import { setOrganizations, setUserType } from '../../redux/Common/commonActions';
import ORGDialogbox from '../../components/ORGDialogbox';
import ApiConstants from './../../config.json';
import Signup from './SignupNew';
import { Animated } from 'react-animated-css';

const environment = ApiConstants;

const Login = () => {
  const dispatch = useDispatch();
  const stateRef = useSelector((state) => state);

  const [redirect, setRedirect] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    otp: '',
    phone: '',
    organization: '',
    username: '',
    UserRole: '',
    type: 'organization',
    city: '',
    state: '',
    country: '',
    address1: '',
    address2: '',
    zip: ''
  });

  const [pages, setPages] = useState({
    login: true,
    signup: false,
    forgetPassword: false,
    otp: false
  });

  const [passwordType, setPasswordType] = useState('password');
  const [alert, setAlert] = useState(null);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [socialAuthData, setSocialAuthData] = useState({
    step: 'none',
    provider: null,
    authenticatedEmail: '',
    loading: false
  });

  const isMounted = useRef(true);

  // --- OAuth Integrations ---
  const googleLogin = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      setSocialAuthData(prev => ({ ...prev, step: 'authenticating', loading: true }));
      handleOAuthBackendLogin('google', tokenResponse.access_token);
    },
    onError: error => {
      console.error('Google Login Error:', error);
      setSocialAuthData({ step: 'none', provider: null, authenticatedEmail: '', loading: false });
    }
  });





  // Fallback automated authentication backend pipeline
  const handleOAuthBackendLogin = (provider, mockToken) => {
    const userData = {
      isSocial: true,
      provider: provider,
      token: mockToken
    };
    axios.post(ApiConstants.externals.serverUrl + 'api/auth/login', userData)
      .then((response) => {
        if (!isMounted.current) return;
        if (response.data.status === 'error' || response.data.status === 'warning') {
          setSocialAuthData({ step: 'none', provider: null, authenticatedEmail: '', loading: false });
          setAlert(<div className={`alert alert-${response.data.status === 'error' ? 'danger' : 'warning'}`}><strong>{response.data.message}</strong></div>);
          setTimeout(() => { if (isMounted.current) setAlert(null); }, 3000);
        } else {
          setFormData(prev => ({ ...prev, UserRole: response.data?.data?.UserRole }));
          setSocialAuthData({ step: 'none', provider: null, authenticatedEmail: '', loading: false });
          setAlert(<div className='alert alert-success'><strong>OTP sent to registered Email</strong></div>);
          setPages({ login: false, otp: true });
          setTimeout(() => { if (isMounted.current) setAlert(null); }, 3000);
        }
      })
      .catch((error) => {
        if (!isMounted.current) return;
        setSocialAuthData({ step: 'none', provider: null, authenticatedEmail: '', loading: false });
        const message = error.response?.data || 'Problem with connecting Server';
        setAlert(<div className='alert alert-danger'><strong>{message}</strong></div>);
        setTimeout(() => { if (isMounted.current) setAlert(null); }, 3000);
      });
  };

  useEffect(() => {
    isMounted.current = true;
    const path = window.location.pathname;
    if (path.includes('signup')) {
      setPages({
        login: false,
        signup: true,
        forgetPassword: false,
        otp: false
      });
    }

    const interval = setInterval(() => {
      validateForm();
    }, 1000);

    return () => {
      isMounted.current = false;
      clearInterval(interval);
    };
  }, [formData, pages]);

  const handleChange = (event) => {
    const { id, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value
    }));
  };

  const validateForm = () => {
    if (pages.login) {
      setButtonDisabled(!(formData.email !== '' && formData.password !== ''));
    } else if (pages.signup) {
      const isBasicsDone =
        formData.email !== '' &&
        formData.password !== '' &&
        formData.phone !== '' &&
        formData.type !== '' &&
        formData.username !== '' &&
        formData.address1 !== '' &&
        formData.address2 !== '' &&
        formData.city !== '' &&
        formData.state !== '' &&
        formData.country !== '' &&
        formData.zip !== '';

      if (isBasicsDone) {
        if (formData.type === 'self') {
          setButtonDisabled(false);
        } else {
          setButtonDisabled(formData.organization === '');
        }
      } else {
        setButtonDisabled(true);
      }
    } else if (pages.forgetPassword) {
      setButtonDisabled(formData.email === '');
    } else if (pages.otp) {
      setButtonDisabled(!formData.otp);
    }
  };

  const handleLogin = async (event, isSocial = false, socialEmail = null) => {
    if (event) event.preventDefault();
    setIsLoading(true);
    const emailToUse = socialEmail || formData.email;
    const password = formData.password;
    const encryptedPassword = CryptoJS.AES.encrypt(
      password,
      environment['encryption-key']
    );

    const userData = {
      email: emailToUse,
      password: encryptedPassword.toString(),
      isSocial: isSocial
    };

    axios.post(ApiConstants.externals.serverUrl + 'api/auth/login', userData)
      .then((response) => {
        setIsLoading(false);
        if (!isMounted.current) return;
        if (response.data.status === 'error') {
          setAlert(
            <Animated animationIn='fadeInDown' animationOut='fadeInUp' isVisible={true}>
              <div className='alert alert-danger' style={{ padding: '8px 16px', borderRadius: '6px', fontSize: '13px' }}>
                <strong>{response.data.message}</strong>
              </div>
            </Animated>
          );
          setTimeout(() => { if (isMounted.current) setAlert(null); }, 3000);
        } else if (response.data.status === 'warning') {
          setAlert(
            <Animated animationIn='fadeInDown' animationOut='fadeInUp' isVisible={true}>
              <div className='alert alert-warning' style={{ padding: '8px 16px', borderRadius: '6px', fontSize: '13px' }}>
                <strong>{response.data.message}</strong>
              </div>
            </Animated>
          );
          setTimeout(() => { if (isMounted.current) setAlert(null); }, 3000);
        } else {
          setFormData(prev => ({ ...prev, UserRole: response.data?.data?.UserRole }));
          setAlert(
            <div className='alert alert-success' style={{ padding: '8px 16px', borderRadius: '6px', fontSize: '12px' }}>
              <strong>OTP sent to registered Email</strong>
            </div>
          );
          setPages({ login: false, otp: true });
          setTimeout(() => { if (isMounted.current) setAlert(null); }, 3000);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        if (!isMounted.current) return;
        const message = error.response?.data || 'Problem with connecting Server';
        setAlert(
          <div className='alert alert-danger' style={{ padding: '8px 16px', borderRadius: '6px', fontSize: '13px' }}>
            <strong>{message}</strong>
          </div>
        );
        setTimeout(() => { if (isMounted.current) setAlert(null); }, 3000);
      });
  };

  const getUserProfile = (tenantId) => {
    const userData = { tenantId };
    localStorage.setItem('tenant_id', tenantId);
    setButtonDisabled(true);
    dispatch(login(userData));
    setButtonDisabled(false);
  };

  const onRegister = (event) => {
    event.preventDefault();
    setButtonDisabled(true);
    axios.post(ApiConstants.externals.serverUrl + 'api/user/register', formData)
      .then((response) => {
        if (!isMounted.current) return;
        if (response.data.status === 'error') {
          const msg = response.data.message.response ? response.data.message.response : response.data.message;
          setAlert(<div className='alert alert-danger'><strong>{msg}</strong></div>);
          setTimeout(() => { if (isMounted.current) setAlert(null); }, 3000);
        } else {
          setAlert(
            <div className='alert alert-success'>
              <strong>Verification email sent.</strong>
            </div>
          );
          localStorage.setItem('tempTenant', response.data.data.tempTenant);
          localStorage.setItem('tempOrg', response.data.data.tempOrg);
          setTimeout(() => { if (isMounted.current) setAlert(null); }, 3000);
          setButtonDisabled(false);
        }
      });
  };

  const onForgot = (event) => {
    event.preventDefault();
    setButtonDisabled(true);
    const payload = { email: formData.email };
    axios.post(ApiConstants.externals.serverUrl + 'api/auth/passwordReset', payload)
      .then((response) => {
        if (!isMounted.current) return;
        if (response.data.status === 'error') {
          setAlert(<div className='alert alert-danger'><strong>{response.data.message}</strong></div>);
        } else {
          setAlert(<div className='alert alert-success'><strong>Instructions sent.</strong></div>);
          setTimeout(() => { if (isMounted.current) setRedirect('/change-password'); }, 3000);
        }
        setTimeout(() => { if (isMounted.current) setAlert(null); }, 3000);
        setButtonDisabled(false);
      })
      .catch(err => console.log(err));
  };

  const togglePassword = () => {
    setPasswordType(passwordType === 'password' ? 'text' : 'password');
  };

  const navigator = (nav) => {
    setButtonDisabled(true);
    if (nav === 'signup') {
      setPages({ login: false, signup: true, forgetPassword: false, otp: false });
    } else if (nav === 'login') {
      setPages({ login: true, signup: false, forgetPassword: false, otp: false });
    } else if (nav === 'forgetPassword') {
      setPages({ login: false, signup: false, forgetPassword: true, otp: false });
    }
  };

  const hideOrgModal = () => setShowModal(false);

  const handleSocialClick = (provider) => {
    setSocialAuthData({ step: 'confirm_provider', provider, authenticatedEmail: '', loading: false });
  };

  const handleSocialConfirm = () => {
    if (socialAuthData.provider === 'google') {
      googleLogin();
    }
  };


  const handleFinalSocialConfirm = () => {
    const email = socialAuthData.authenticatedEmail;
    setSocialAuthData(prev => ({ ...prev, step: 'none', loading: false }));
    setFormData(prev => ({ ...prev, email: email }));
    handleLogin(null, true, email);
  };

  const handleCallback = (input) => {
    if (input === 'signup') {
      setButtonDisabled(true);
      setPages({ login: true, signup: false, forgetPassword: false, otp: false });
    }
  };

  const handleOTP = (event) => {
    event.preventDefault();
    setIsLoading(true);
    const userData = {
      otp: formData.otp,
      UserRole: formData.UserRole
    };
    axios.post(ApiConstants.externals.serverUrl + 'api/auth/verifyOTP', userData, { withCredentials: true })
      .then((response) => {
        setIsLoading(false);
        if (!isMounted.current) return;
        if (response.data.status === 'error') {
          setAlert(
            <Animated animationIn='fadeInDown' animationOut='fadeInUp' isVisible={true}>
              <div className='alert alert-danger'><strong>{response.data.message}</strong></div>
            </Animated>
          );
          setTimeout(() => { if (isMounted.current) setAlert(null); }, 3000);
        } else {
          const noOfModules = response.data.data.org.length;
          if (noOfModules > 0) {
            if (noOfModules === 1) {
              getUserProfile(response.data.data.org[0].Tenant_ID);
            } else {
              dispatch(setOrganizations(response.data.data.org));
              dispatch(setUserType(response.data.data.user_type));
              setShowModal(true);
            }
          }
        }
      })
      .catch(() => {
        setIsLoading(false);
        if (!isMounted.current) return;
        setAlert(<div className='alert alert-danger'><strong>Problem connecting to Server</strong></div>);
        setTimeout(() => { if (isMounted.current) setAlert(null); }, 3000);
      });
  };

  if (redirect) return <Redirect to={redirect} />;

  const LabelTypography = ({ children }) => (
    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#64748b', mb: 0.8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {children}
    </Typography>
  );

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overflow: 'hidden',
      background: 'linear-gradient(135deg, #1e40af 0%, #ffffff 100%)',
    }}>
      {/* Dynamic Background Image Overlay */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        opacity: 0.08,
        zIndex: 0
      }} />

      {/* Main Content Area */}
      <Box component="main" sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, position: 'relative', zIndex: 1 }}>
        <Fade in timeout={600}>
          <Box sx={{ width: '100%', maxWidth: pages.signup ? 800 : 400 }}>
            {/* Main Login Card - THEME DRIVEN */}
            <Card
              sx={{
                borderTop: '6px solid #1e40af',
                overflow: 'visible'
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

                {/* ADVANCED HEADER SECTION */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                  <Box sx={{ mb: 1.5 }}>
                    <img src={logo} alt="DataTwin" style={{ height: 40, width: 40 }} />
                  </Box>
                  <Typography variant="h4" sx={{ color: 'text.primary', mb: 0.5, fontSize: '1.75rem', lineHeight: 1.1, textAlign: 'center', fontWeight: 800 }}>
                    {pages.login ? 'Sign In' : pages.otp ? 'Verification' : pages.signup ? 'Sign Up' : 'Password Reset'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', fontWeight: 600 }}>
                    to access DataTwin
                  </Typography>
                </Box>

                <Box component="form" noValidate sx={{ width: '100%' }}>
                  {pages.login && (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <LabelTypography>Email Address</LabelTypography>
                        <TextField
                          fullWidth
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="name@company.com"
                          sx={{ '& input': { py: 1.2, fontSize: '0.95rem' } }}
                        />
                      </Box>

                      <Box sx={{ mb: 2.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <LabelTypography>Password</LabelTypography>
                          <Button variant="text" size="small" onClick={() => navigator('forgetPassword')}
                            sx={{ color: 'primary.main', fontSize: '11px', p: 0, mb: 1, '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}>
                            Forgot Password?
                          </Button>
                        </Box>
                        <TextField
                          fullWidth
                          id="password"
                          type={passwordType}
                          value={formData.password}
                          onChange={handleChange}
                          sx={{ '& input': { py: 1.2, fontSize: '0.95rem' } }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={togglePassword} edge="end" size="small">
                                  {passwordType === 'password' ? <VisibilityIcon sx={{ fontSize: 20 }} /> : <VisibilityOffIcon sx={{ fontSize: 20 }} />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>

                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleLogin}
                        disabled={buttonDisabled || isLoading}
                        sx={{ py: 1.2, fontSize: '15px', mb: 2.5 }}
                      >
                        Sign In
                      </Button>

                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Paper
                          variant="outlined"
                          sx={{
                            py: 1.5,
                            px: 4,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.25s',
                            borderRadius: '12px',
                            '&:hover': { bgcolor: '#f8fafc', borderColor: 'primary.main', boxShadow: '0 4px 12px rgba(30, 64, 175, 0.1)' }
                          }}
                          onClick={() => handleSocialClick('google')}
                        >
                          <svg style={{ height: 24, width: 24, transition: 'transform 0.25s', marginRight: '12px' }} onMouseEnter={e => e.target.style.transform = 'scale(1.2)'} onMouseLeave={e => e.target.style.transform = 'scale(1)'} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                            <path d="M12 23c3.08 0 5.68-.98 7.57-2.66l-3.57-2.77c-1.01.68-2.31 1.09-4.01 1.09-3.08 0-5.69-2.08-6.62-4.88H1.67v2.86C3.55 19.33 7.51 23 12 23z" fill="#34A853" />
                            <path d="M5.38 13.78C5.14 13.08 5 12.33 5 11.5s.14-1.58.38-2.28V6.36H1.67c-.6 1.19-.94 2.53-.94 3.97s.34 2.78.94 3.97l3.71-2.91z" fill="#FBBC05" />
                            <path d="M12 4.75c1.67 0 3.17.58 4.35 1.71l3.27-3.27C17.68 1.48 15.08 0 12 0 7.51 0 3.55 3.67 1.67 8.36l3.71 2.91C6.31 6.83 8.92 4.75 12 4.75z" fill="#EA4335" />
                          </svg>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: 'text.primary' }}>Google</Typography>
                        </Paper>
                      </Box>
                    </>
                  )}

                  {pages.otp && (
                    <Box sx={{ textAlign: 'center' }}>
                      <LabelTypography>Verification Code</LabelTypography>
                      <TextField
                        fullWidth
                        id="otp"
                        type="text"
                        value={formData.otp}
                        onChange={handleChange}
                        placeholder="000000"
                        sx={{
                          '& input': { py: 2.5, px: 2, textAlign: 'center', fontSize: '28px', fontWeight: 'bold', letterSpacing: '0.3em' }
                        }}
                      />
                      <Box sx={{ display: 'flex', gap: 2, mt: 5 }}>
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={handleOTP}
                          disabled={buttonDisabled || isLoading}
                          sx={{ py: 2, fontSize: '16px' }}
                        >
                          Verify OTP
                        </Button>
                        {formData?.UserRole && <Button
                          fullWidth
                          variant="outlined"
                          onClick={handleOTP}
                          sx={{ py: 2, fontSize: '16px', color: 'text.primary' }}
                        >
                          Admin Access
                        </Button>}
                      </Box>
                      <Box sx={{ mt: 4 }}>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => {
                            navigator('login');
                            setFormData(prev => ({ ...prev, otp: '', UserRole: '' }))
                          }}
                          sx={{ color: 'text.secondary', fontSize: '15px' }}
                        >
                          Back to Sign In
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {pages.signup && <Signup parentCallback={handleCallback} />}

                  {pages.forgetPassword && (
                    <Box sx={{ textAlign: 'left' }}>
                      <LabelTypography>Email Address</LabelTypography>
                      <TextField
                        fullWidth
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        sx={{ '& input': { py: 2 } }}
                      />
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={onForgot}
                        disabled={buttonDisabled}
                        sx={{ mt: 4, py: 2, fontSize: '16px' }}
                      >
                        Send Reset Instructions
                      </Button>
                      <Box sx={{ mt: 5, textAlign: 'center' }}>
                        <Button onClick={() => navigator('login')} sx={{ color: 'primary.main', fontSize: '15px' }}>Back to Sign In</Button>
                      </Box>
                    </Box>
                  )}

                  {alert && <Box sx={{ mt: 4 }}>{alert}</Box>}
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Fade>
      </Box>

      {/* Footer Area */}
      <Box sx={{ pt: 2, pb: 6, textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <Typography sx={{ color: '#ffffff', opacity: 0.9, fontSize: '13px', fontWeight: 700, textShadow: '0 1px 4px rgba(0,0,0,0.2)' }}>
          &copy; {new Date().getFullYear()} DataTwin Inc. All rights reserved.
        </Typography>
      </Box>

      {/* Organization Choice Modal */}
      <ORGDialogbox
        open={showModal}
        onClose={hideOrgModal}
      />

      {/* Social Login Multi-Step Dialog */}
      <Dialog
        open={socialAuthData.step !== 'none'}
        onClose={() => setSocialAuthData({ step: 'none', provider: null, authenticatedEmail: '', loading: false })}
        PaperProps={{
          sx: { borderRadius: '20px', p: 2, minWidth: 400, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)' }
        }}
      >
        <Fade in timeout={400}>
          <Box>
            <DialogTitle sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pb: 1 }}>
              <Box sx={{ mb: 2, p: 1.5, borderRadius: '16px', bgcolor: 'rgba(30, 64, 175, 0.04)' }}>
                <img src={logo} alt="DataTwin" style={{ height: 48, filter: socialAuthData.step === 'authenticating' ? 'grayscale(1) opacity(0.5)' : 'none', transition: 'all 0.5s' }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.025em' }}>
                {socialAuthData.step === 'confirm_provider' && `Sign in with ${socialAuthData.provider?.charAt(0).toUpperCase() + socialAuthData.provider?.slice(1)}`}
                {socialAuthData.step === 'authenticating' && `Connecting to ${socialAuthData.provider?.toUpperCase()}...`}
                {socialAuthData.step === 'confirm_email' && `Verify Your Identity`}
              </Typography>
            </DialogTitle>

            <DialogContent sx={{ mt: 2 }}>
              {socialAuthData.step === 'confirm_provider' && (
                <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontSize: '1.05rem', lineHeight: 1.5 }}>
                  You are about to be redirected to <strong>{socialAuthData.provider?.toUpperCase()}</strong> for secure authentication. Do you wish to continue?
                </Typography>
              )}

              {socialAuthData.step === 'authenticating' && (
                <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                  <CircularProgress size={48} sx={{ color: '#1e40af', thickness: 5 }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    Please wait while we verify your credentials with the provider.
                  </Typography>
                </Box>
              )}

              {socialAuthData.step === 'confirm_email' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Typography sx={{ textAlign: 'center', color: 'text.secondary', mb: 3 }}>
                    Authentication Successful! We retrieved the following account:
                  </Typography>
                  <Paper variant="outlined" sx={{
                    p: 2.5,
                    width: '100%',
                    bgcolor: '#f8fafc',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }}>
                    <Avatar sx={{ bgcolor: '#1e40af', width: 40, height: 40 }}>
                      {socialAuthData.authenticatedEmail?.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a' }}>
                        {socialAuthData.authenticatedEmail}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        {socialAuthData.provider?.toUpperCase()} Verified
                      </Typography>
                    </Box>
                  </Paper>
                  <Typography variant="caption" sx={{ mt: 2, textAlign: 'center', display: 'block', color: 'text.secondary' }}>
                    Confirm to link this account and access your DataTwin dashboard.
                  </Typography>
                </Box>
              )}
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'center', pb: 2, pt: 2, gap: 1.5 }}>
              {socialAuthData.step !== 'authenticating' && (
                <>
                  <Button
                    onClick={() => setSocialAuthData({ step: 'none', provider: null, authenticatedEmail: '', loading: false })}
                    sx={{ px: 3, borderRadius: '10px', fontWeight: 600, color: 'text.secondary' }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={
                      socialAuthData.step === 'confirm_provider' ? handleSocialConfirm : handleFinalSocialConfirm
                    }
                    variant="contained"
                    sx={{ px: 4, borderRadius: '10px', fontWeight: 700, boxShadow: '0 4px 12px rgba(30, 64, 175, 0.2)' }}
                  >
                    {socialAuthData.step === 'confirm_provider' ? 'Yes, Continue' : 'Confirm & Login'}
                  </Button>
                </>
              )}
            </DialogActions>
          </Box>
        </Fade>
      </Dialog>
    </Box>
  );
};

export default function LoginWithOAuth() {
  return (
    <GoogleOAuthProvider clientId={ApiConstants.GOOGLE_CLIENT_ID || 'SET_YOUR_GOOGLE_CLIENT_ID_IN_CONFIG'}>
      <Login />
    </GoogleOAuthProvider>
  );
}

